// EST monthly bonus — client for GET/PUT /api/v1/payroll/est-bonus
// (src/est_bonus.js; rules payroll/decisions/LOG.md 2026-10-05). The bonus is
// computed on the server (src/est_bonus_calc.js) and only displayed here — one
// copy of the math.
import { authedGet, authedWrite } from './apiClient';
import { clinicToday } from './clinicDay';
import { submitManagerEntry } from './managerEntry';

export interface EstBonusInputs {
  serviceHours: number | null;
  serviceCount: number | null;
  ascCount: number | null;
  enhancementCount: number | null;
  retailNet: number | null;
}

export interface EstBonusLine {
  key: string;
  label: string;
  amount: number;
  earned: boolean;
  /** "advanced skincare 54.5% (60 of 110 services) — needs 25%" */
  test?: string;
  /** "60 × $4.00 = $240.00" */
  formula?: string;
}

export interface EstBonusComputed {
  ready: boolean;
  missing: string[];
  total: number | null;
  lines: EstBonusLine[];
  rates: { ascPct: number; enhPct: number; retailAvg: number } | null;
  fullTime: boolean | null;
  /** 45+ service hours on EACH of the last two paychecks (owner, 2026-10-05). */
  fullTimeTest?: {
    fullTime: boolean | null;
    paychecks: Array<{ adpWeek: number; hours: number | null }>;
    needs: number;
    short: number[];
  };
  fullTimeOverride?: boolean | null;
  /** Already on the program — the service-hour requirement is on hold (owner, 2026-10-05). */
  fullTimeOnHold?: boolean;
  retailMinimumMet?: boolean;
  earnedBeforeFullTime?: number;
}

export interface EstBonusPerson {
  employeeId: number;
  name: string;
  fileNo: string | null;
  employmentType: string | null;
  rowId: number | null;
  inputs: EstBonusInputs;
  source: 'MEEVO' | 'MANUAL' | 'SHEET' | null;
  sourceRef: string | null;
  meevoValues: EstBonusInputs | null;
  fullTimeOverride: boolean | null;
  fullTimeReason: string | null;
  paidAmount: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
  computed: EstBonusComputed;
  /** History rows: the plan rules run on the calc sheet's own numbers. */
  sheetCalc?: EstBonusComputed | null;
  sent: { lineId: number; amount: number; enteredBy: string; enteredAt: string } | null;
}

export interface EstBonusPayroll {
  payPeriodId: number;
  periodCode: string;
  adpWeek: number;
  startDate: string;
  endDate: string;
  closed: boolean;
  monthLastDay: string;
}

export interface EstBonusMonth {
  locationCode: string;
  year: number;
  month: number;
  monthLabel: string;
  plan: { name: string; startYear: number; startMonth: number } | null;
  covered: boolean;
  bonusPayroll: EstBonusPayroll | null;
  /** The two paychecks full time is judged on. */
  fullTimePaychecks?: Array<{ adpWeek: number; startDate: string; endDate: string; registerLoaded: boolean }>;
  reason: string;
  people: EstBonusPerson[];
}

/** Clinics on the plan (src/est_bonus_calc.js EST_BONUS_PLANS — the server is
 * the authority; this only decides whether the tab is drawn at all). */
export const EST_BONUS_CLINICS: Record<string, { startYear: number; startMonth: number }> = {
  MEVL: { startYear: 2026, startMonth: 1 },
  MEWG: { startYear: 2026, startMonth: 5 },
};

export function estBonusKey(location: string, year: number, month: number): string {
  return `est-bonus:${location}:${year}-${String(month).padStart(2, '0')}`;
}

export function fetchEstBonusMonth(location: string, year: number, month: number): Promise<EstBonusMonth> {
  const p = new URLSearchParams({ location, year: String(year), month: String(month) });
  return authedGet<EstBonusMonth>(`/api/v1/payroll/est-bonus?${p.toString()}`);
}

export function saveEstBonusMonth(input: {
  locationCode: string; employeeId: number; year: number; month: number;
  fullTimeOverride?: boolean | null; fullTimeReason?: string;
} & EstBonusInputs): Promise<unknown> {
  return authedWrite('PUT', '/api/v1/payroll/est-bonus', input);
}

/** "Undo edit" (N-071): an edited month back to the Meevo numbers kept
 * beside it. The row stays; the server refuses when there is nothing to go
 * back to, or (for a manager) once the month is on Manager Entry. */
export function revertEstBonusMonth(input: {
  locationCode: string; employeeId: number; year: number; month: number;
}, key: string = crypto.randomUUID()): Promise<unknown> {
  return authedWrite('POST', '/api/v1/payroll/est-bonus/revert', input, key);
}

/** The bonuses go to ADP as Manager Entry BONUS lines on the bonus payroll —
 * the same submission, guards and record as any other bonus. */
export function sendEstBonusesToManagerEntry(
  month: EstBonusMonth,
  people: EstBonusPerson[],
  key: string,
): Promise<unknown> {
  if (!month.bonusPayroll) throw new Error('no bonus payroll for this month');
  return submitManagerEntry({
    locationCode: month.locationCode,
    payPeriodId: month.bonusPayroll.payPeriodId,
    entries: {
      bonus: people.map((p) => ({ employeeId: p.employeeId, amount: p.computed.total, detail: month.reason })),
    },
  }, key);
}

/** The last month that has ended — what the tab opens on. */
export function lastFinishedMonth(today = new Date()): { year: number; month: number } {
  // The clinic's (Eastern) calendar month, not the viewer's computer.
  const [y, m] = clinicToday(today).split('-').map(Number); // m = this month
  return m === 1 ? { year: y - 1, month: 12 } : { year: y, month: m - 1 };
}
